import React from 'react';

interface SkeletonLoaderProps {
  variant?: 'text' | 'card' | 'table' | 'quiz' | 'prospect' | 'custom';
  lines?: number;
  height?: string;
  width?: string;
  className?: string;
  animate?: boolean;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant = 'text',
  lines = 3,
  height = 'h-4',
  width = 'w-full',
  className = '',
  animate = true
}) => {
  const baseClasses = `bg-gray-200 rounded ${animate ? 'animate-pulse' : ''}`;
  
  const renderTextSkeleton = () => (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`${baseClasses} ${height} ${
            index === lines - 1 ? 'w-3/4' : width
          }`}
        />
      ))}
    </div>
  );

  const renderCardSkeleton = () => (
    <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>
      <div className={`${baseClasses} h-6 w-1/2 mb-4`} />
      <div className="space-y-3">
        <div className={`${baseClasses} h-4 w-full`} />
        <div className={`${baseClasses} h-4 w-5/6`} />
        <div className={`${baseClasses} h-4 w-3/4`} />
      </div>
      <div className={`${baseClasses} h-10 w-32 mt-6`} />
    </div>
  );

  const renderTableSkeleton = () => (
    <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <div className="flex space-x-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className={`${baseClasses} h-4 w-24`} />
          ))}
        </div>
      </div>
      {/* Rows */}
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <div key={rowIndex} className="px-6 py-4 border-b last:border-b-0">
          <div className="flex space-x-4">
            {Array.from({ length: 4 }).map((_, colIndex) => (
              <div key={colIndex} className={`${baseClasses} h-4 w-24`} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const renderQuizSkeleton = () => (
    <div className={`space-y-6 ${className}`}>
      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex justify-between">
          <div className={`${baseClasses} h-4 w-16`} />
          <div className={`${baseClasses} h-4 w-12`} />
        </div>
        <div className={`${baseClasses} h-2 w-full`} />
      </div>
      
      {/* Question card */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <div className={`${baseClasses} h-6 w-3/4 mb-6`} />
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className={`${baseClasses} h-12 w-full rounded-lg`} />
          ))}
        </div>
      </div>
    </div>
  );

  const renderProspectSkeleton = () => (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className={`${baseClasses} h-8 w-48`} />
        <div className={`${baseClasses} h-10 w-32`} />
      </div>
      
      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border p-4">
            <div className={`${baseClasses} h-5 w-3/4 mb-3`} />
            <div className="space-y-2">
              <div className={`${baseClasses} h-4 w-full`} />
              <div className={`${baseClasses} h-4 w-2/3`} />
            </div>
            <div className="flex justify-between items-center mt-4">
              <div className={`${baseClasses} h-6 w-16 rounded-full`} />
              <div className={`${baseClasses} h-8 w-20`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCustomSkeleton = () => (
    <div className={`${baseClasses} ${height} ${width} ${className}`} />
  );

  switch (variant) {
    case 'card':
      return renderCardSkeleton();
    case 'table':
      return renderTableSkeleton();
    case 'quiz':
      return renderQuizSkeleton();
    case 'prospect':
      return renderProspectSkeleton();
    case 'custom':
      return renderCustomSkeleton();
    default:
      return renderTextSkeleton();
  }
};

export default SkeletonLoader;
